import axiosInstance from "./axiosInstance";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ChatConversationSummary {
  id: number;
  title: string;
  updatedAt: string;
  messages: Array<{ role: string; content: string }>;
}

export interface ChatConversationDetail {
  id: number;
  title: string;
  messages: ChatMessage[];
}

export type ChatStreamEvent =
  | { type: "status"; text: string }
  | { type: "token"; text: string }
  | { type: "done"; conversationId?: number }
  | { type: "error"; message: string };

export interface PageContext {
  type: "deal" | "company";
  id: number;
}

export interface ChatAttachment {
  /** Raw base64-encoded file content (no data URI prefix) */
  data: string;
  mimeType: string;
  fileName: string;
}

export async function sendChatMessage(
  messages: ChatMessage[],
  conversationId?: number,
  signal?: AbortSignal,
  onEvent?: (event: ChatStreamEvent) => void,
  pageContext?: PageContext | null,
  attachment?: ChatAttachment | null
): Promise<{ response: string; conversationId?: number }> {
  const serverUrl = process.env.REACT_APP_SERVER_URL ?? "";
  const url = `${serverUrl}/chat`;

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      messages,
      conversationId,
      pageContext: pageContext ?? null,
      ...(attachment ? { attachment } : {}),
    }),
    signal,
  });

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let fullResponse = "";
  let finalConversationId: number | undefined = conversationId;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse complete SSE lines
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? ""; // Keep incomplete last line in buffer

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      try {
        const event = JSON.parse(line.slice(6)) as ChatStreamEvent;
        onEvent?.(event);
        if (event.type === "token") {
          fullResponse += event.text;
        } else if (event.type === "status") {
          // Mirror what streamingText does in the component: clear accumulated text on
          // status events so planning/intermediate tokens don't end up in the stored message.
          fullResponse = "";
        } else if (event.type === "done") {
          if ((event as any).conversationId != null) {
            finalConversationId = (event as any).conversationId;
          }
        } else if (event.type === "error") {
          throw new Error(event.message);
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue; // Skip malformed SSE lines
        throw e;
      }
    }
  }

  return { response: fullResponse, conversationId: finalConversationId };
}

export async function listConversations(): Promise<ChatConversationSummary[]> {
  const res = await axiosInstance.get<ChatConversationSummary[]>("/chat/conversations");
  return res.data;
}

export async function createConversation(title: string): Promise<{ id: number; title: string }> {
  const res = await axiosInstance.post<{ id: number; title: string }>("/chat/conversations", { title });
  return res.data;
}

export async function getConversation(id: number): Promise<ChatConversationDetail> {
  const res = await axiosInstance.get<ChatConversationDetail>(`/chat/conversations/${id}`);
  return res.data;
}

export async function deleteConversation(id: number): Promise<void> {
  await axiosInstance.delete(`/chat/conversations/${id}`);
}

export async function renameConversation(id: number, title: string): Promise<void> {
  await axiosInstance.patch(`/chat/conversations/${id}`, { title });
}
